/**
 * Talk-o 前端 API 封装
 *
 * 认证相关接口通过 HTTP-only Cookie 传递 token，
 * 所有请求带 credentials，确保跨域/代理环境下 Cookie 正确发送。
 */

const API_BASE = ''

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    ...options,
  })

  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    try {
      const body = await response.json()
      if (body.detail) detail = body.detail
    } catch {
      const text = await response.text()
      if (text) detail = text
    }
    throw new Error(detail)
  }

  if (response.status === 204) {
    return undefined as unknown as T
  }

  return (await response.json()) as T
}

// -----------------------------------------------------------------------------
// 会员认证
// -----------------------------------------------------------------------------

export const registerEmail = (email: string) =>
  apiFetch<{ success: boolean; message: string }>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })

export const verifyEmailToken = (token: string) =>
  apiFetch<{ valid: boolean; email: string }>('/api/auth/verify-token', {
    method: 'POST',
    body: JSON.stringify({ token }),
  })

export const setCredentials = (token: string, username: string, password: string) =>
  apiFetch<{ success: boolean; user: { id: string; email: string; username: string } }>(
    '/api/auth/set-credentials',
    {
      method: 'POST',
      body: JSON.stringify({ token, username, password }),
    }
  )

export const login = (username: string, password: string) =>
  apiFetch<{ success: boolean; user: { id: string; email: string; username: string } }>(
    '/api/auth/login',
    {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }
  )

export const logout = () => apiFetch<{ success: boolean }>('/api/auth/logout', { method: 'POST' })

export const getMe = () => apiFetch<{ user: { id: string; email: string; username: string | null; is_verified: boolean; created_at: string | null } }>('/api/auth/me')

// -----------------------------------------------------------------------------
// 会员用量
// -----------------------------------------------------------------------------

export interface UsageSummary {
  user: { id: string; email: string; username: string | null; is_verified: boolean; created_at: string | null }
  chat_turns: number
  usage_by_feature: Record<
    string,
    { sessions: number; duration_seconds: number; input_bytes: number; output_bytes: number }
  >
}

export const getMemberUsage = () => apiFetch<UsageSummary>('/api/member/usage')

// -----------------------------------------------------------------------------
// 管理员后台
// -----------------------------------------------------------------------------

export const adminLogin = (username: string, password: string) =>
  apiFetch<{ success: boolean; admin: { id: string; username: string } }>('/api/membership/admin/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  })

export const adminLogout = () =>
  apiFetch<{ success: boolean }>('/api/membership/admin/logout', { method: 'POST' })

export const getAdminMe = () =>
  apiFetch<{ admin: { id: string; username: string } }>('/api/membership/admin/me')

export interface MemberWithUsage {
  id: string
  email: string
  username: string | null
  is_verified: boolean
  created_at: string | null
  chat_turns: number
  total_duration_seconds: number
  total_input_bytes: number
  total_output_bytes: number
}

export const listMembers = () =>
  apiFetch<{ members: MemberWithUsage[] }>('/api/membership/admin/members')

export const getMemberDetail = (userId: string) =>
  apiFetch<{
    user: { id: string; email: string; username: string | null; is_verified: boolean; created_at: string | null }
    usage_events: Array<{
      id: string
      feature: string
      duration_seconds: number | null
      input_bytes: number | null
      output_bytes: number | null
      created_at: string | null
    }>
    chat_logs: Array<{
      id: number
      timestamp: string | null
      kid_text: string
      ai_text: string
      audio_file_path: string
      ai_audio_file_path: string | null
      persona_id: number | null
    }>
  }>(`/api/membership/admin/members/${userId}`)
